import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import dayjs from "dayjs";

const db = firebase.firestore();

/**
 * Hook to fetch and manage sales aggregates data from Firestore
 * @param {Object} options - Configuration options
 * @param {number} options.startDateUnix - Start date in unix timestamp (optional)
 * @param {number} options.endDateUnix - End date in unix timestamp (optional)
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @returns {Object} - Sales data and utility functions
 */
export const useSalesAggregates = (options = {}) => {
  const { startDateUnix = null, endDateUnix = null, autoFetch = true } = options;

  const [salesData, setSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch sales aggregates for a specific period
   * @param {number} startUnix - Start date in unix timestamp
   * @param {number} endUnix - End date in unix timestamp
   * @returns {Promise<Array>} - Array of sales aggregate documents
   */
  const fetchSalesData = useCallback(async (startUnix, endUnix) => {
    setIsLoading(true);
    setError(null);

    try {
      const salesRef = db.collection("salesAggregates");
      const snapshot = await salesRef
        .where("dateUnix", ">=", startUnix)
        .where("dateUnix", "<=", endUnix)
        .orderBy("dateUnix")
        .get();

      if (snapshot.empty) {
        setSalesData([]);
        return [];
      }

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setSalesData(data);
      return data;
    } catch (err) {
      console.error("Error fetching sales data:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Calculate product sales from sales aggregates
   * @param {Array} salesAggregates - Array of sales aggregate documents
   * @returns {Object} - Object with productId as key and sales data as value
   */
  const calculateProductSales = useCallback((salesAggregates) => {
    const productSales = {};

    salesAggregates.forEach((aggregate, index) => {
      if (aggregate.products && typeof aggregate.products === "object") {
        const productIds = Object.keys(aggregate.products);

        // Products is an object with productId as key
        productIds.forEach((productId) => {
          const product = aggregate.products[productId];

          if (!productSales[productId]) {
            productSales[productId] = {
              productId,
              soldQuantity: 0,
              revenue: 0,
              orderCount: 0,
            };
          }

          const quantityToAdd = product.quantity || 0;
          const revenueToAdd = product.totalValue || 0;

          // The structure is: { name, quantity, totalValue }
          productSales[productId].soldQuantity += quantityToAdd;
          productSales[productId].revenue += revenueToAdd;
          productSales[productId].orderCount += 1;
        });
      } else {
        console.log(`Aggregate ${index}: no products field or wrong type`);
      }
    });
    return productSales;
  }, []);

  /**
   * Get sales data for a specific period from already loaded data
   * @param {number} startUnix - Start date in unix timestamp
   * @param {number} endUnix - End date in unix timestamp
   * @returns {Array} - Filtered sales data
   */
  const getSalesInPeriod = useCallback(
    (startUnix, endUnix) => {
      return salesData.filter((sale) => sale.dateUnix >= startUnix && sale.dateUnix <= endUnix);
    },
    [salesData],
  );

  /**
   * Get product sales for a specific period
   * @param {number} startUnix - Start date in unix timestamp
   * @param {number} endUnix - End date in unix timestamp
   * @returns {Object} - Product sales data
   */
  const getProductSalesInPeriod = useCallback(
    async (startUnix, endUnix) => {
      // Always fetch fresh data for the requested period
      const fetchedData = await fetchSalesData(startUnix, endUnix);
      // Use the fetched data directly instead of waiting for state update
      const periodData = fetchedData.filter((sale) => sale.dateUnix >= startUnix && sale.dateUnix <= endUnix);
      return calculateProductSales(periodData);
    },
    [calculateProductSales, fetchSalesData],
  );

  // Auto-fetch on mount if enabled and dates are provided
  useEffect(() => {
    if (autoFetch && startDateUnix && endDateUnix) {
      fetchSalesData(startDateUnix, endDateUnix);
    }
  }, [autoFetch, startDateUnix, endDateUnix, fetchSalesData]);

  return {
    salesData,
    isLoading,
    error,
    fetchSalesData,
    calculateProductSales,
    getSalesInPeriod,
    getProductSalesInPeriod,
  };
};

/**
 * Helper function to get default date range (1 year back)
 * @returns {Object} - Object with startDateUnix and endDateUnix
 */
export const getDefaultDateRange = () => {
  const yearStartDate = dayjs().subtract(1, "year").startOf("day").unix();
  const yearEndDate = dayjs().subtract(1, "day").endOf("day").unix();

  return {
    startDateUnix: yearStartDate,
    endDateUnix: yearEndDate,
  };
};
